// src/pages/QbusAutomations/NodeDataDrawer.jsx
// n8n-style node inspector with Input Data | Config | Output Data tabs
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Input as InputIcon,
  Output as OutputIcon,
  Settings as ConfigIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import NodeConfigPanel from './NodeConfigPanel';
import { VarTreeNode } from './DebugPanel';

const categoryColors = {
  trigger: '#ef6c00',
  condition: '#1976d2',
  action: '#388e3c',
};

const NodeDataDrawer = ({
  node,
  executionStep,
  locationId,
  debugMode,
  onConfigChange,
  onDelete,
  onClose,
}) => {
  // Default to Config tab if no debug data, or Input Data if debug mode
  const [activeTab, setActiveTab] = useState(debugMode && executionStep ? 0 : 1);

  const hasDebugData = debugMode && executionStep;
  const headerColor = categoryColors[node.type] || '#666';

  // Compute variable diff (what changed between input and output)
  const changedVars = useMemo(() => {
    if (!executionStep?.inputVariables || !executionStep?.outputVariables) return new Set();
    const changed = new Set();
    const input = executionStep.inputVariables;
    const output = executionStep.outputVariables;

    // Check for new or modified keys
    for (const key of Object.keys(output)) {
      if (key.includes('.') || key.startsWith('_')) continue;
      if (!(key in input)) {
        changed.add(key);
      } else if (JSON.stringify(input[key]) !== JSON.stringify(output[key])) {
        changed.add(key);
      }
    }
    return changed;
  }, [executionStep]);

  // Filter variables to only show top-level (skip dot-notation and internal keys)
  const filterVars = (vars) => {
    if (!vars) return {};
    const filtered = {};
    for (const [key, value] of Object.entries(vars)) {
      if (key.includes('.') || key.startsWith('_')) continue;
      filtered[key] = value;
    }
    return filtered;
  };

  const inputVars = filterVars(executionStep?.inputVariables);
  const outputVars = filterVars(executionStep?.outputVariables);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: headerColor,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {node.data.label}
          </Typography>
          {hasDebugData && (
            <Chip
              icon={executionStep.status === 'completed'
                ? <SuccessIcon sx={{ fontSize: 12, color: 'white !important' }} />
                : <ErrorIcon sx={{ fontSize: 12, color: 'white !important' }} />}
              label={executionStep.status}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <IconButton size="small" sx={{ color: 'white' }} onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Tab bar */}
      {hasDebugData ? (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, fontSize: '0.7rem', textTransform: 'none', py: 0 },
          }}
        >
          <Tab icon={<InputIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Input" />
          <Tab icon={<ConfigIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Config" />
          <Tab icon={<OutputIcon sx={{ fontSize: 14 }} />} iconPosition="start" label="Output" />
        </Tabs>
      ) : null}

      <Divider />

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* INPUT DATA TAB */}
        {hasDebugData && activeTab === 0 && (
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block', px: 0.5 }}>
              Variables available to this node
            </Typography>
            {Object.keys(inputVars).length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                No variables at this point in the flow
              </Typography>
            ) : (
              Object.entries(inputVars).map(([key, value]) => (
                <VarTreeNode
                  key={key}
                  name={key}
                  value={value}
                  path={key}
                  depth={0}
                />
              ))
            )}
          </Box>
        )}

        {/* CONFIG TAB (or default when no debug data) */}
        {((!hasDebugData) || activeTab === 1) && (
          <Box sx={{ p: 2 }}>
            <NodeConfigPanel
              node={node}
              locationId={locationId}
              onChange={onConfigChange}
            />
          </Box>
        )}

        {/* OUTPUT DATA TAB */}
        {hasDebugData && activeTab === 2 && (
          <Box sx={{ p: 1 }}>
            {/* Node execution output */}
            {executionStep.output && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block', px: 0.5 }}>
                  Block Output
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5' }}>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ fontFamily: 'monospace', fontSize: '0.65rem', m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                  >
                    {typeof executionStep.output === 'object'
                      ? JSON.stringify(executionStep.output, null, 2)
                      : String(executionStep.output)}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Error */}
            {executionStep.error && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color: '#c62828', mb: 0.5, display: 'block', px: 0.5 }}>
                  ERROR
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffebee', borderColor: '#ef9a9a' }}>
                  <Typography variant="caption" sx={{ color: '#c62828', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                    {executionStep.error}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Variables changed by this node */}
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, display: 'block', px: 0.5 }}>
              Variables after this node
              {changedVars.size > 0 && (
                <Chip
                  label={`${changedVars.size} changed`}
                  size="small"
                  color="success"
                  sx={{ ml: 1, height: 16, fontSize: '0.55rem' }}
                />
              )}
            </Typography>
            {Object.keys(outputVars).length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
                No variables
              </Typography>
            ) : (
              Object.entries(outputVars).map(([key, value]) => (
                <VarTreeNode
                  key={key}
                  name={key}
                  value={value}
                  path={key}
                  depth={0}
                  highlightPaths={changedVars}
                />
              ))
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NodeDataDrawer;
